import ContentPasteRoundedIcon from '@mui/icons-material/ContentPasteRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, TextField, Typography } from '@mui/material';
import React, { ChangeEvent, Component, createRef } from 'react';

import { ProgramWeeks } from '../../api/programs.interface';
import { normalizeProgramNameInput } from './programs.utils';
import { parseProgramImportFile } from './programImport.utils';
import { ProgramImportParseResult } from './programs.types';

interface Props {
    existingProgramNames: string[];
    onCancel: () => void;
    onImport: (name: string, weeks: ProgramWeeks) => void;
    visible: boolean;
}

interface State {
    errorMessage?: string;
    fileName?: string;
    importName: string;
    importNameError?: string;
    isReadingClipboard: boolean;
    isReadingFile: boolean;
    parseResult?: ProgramImportParseResult;
    pastedContent: string;
    parsedSourceLabel?: string;
}

class ProgramImportDialog extends Component<Props, State> {
    private readonly fileInputRef = createRef<HTMLInputElement>();

    state: State = {
        errorMessage: undefined,
        fileName: undefined,
        importName: '',
        importNameError: undefined,
        isReadingClipboard: false,
        isReadingFile: false,
        parseResult: undefined,
        pastedContent: '',
        parsedSourceLabel: undefined,
    };

    componentDidUpdate(prevProps: Props) {
        if (!prevProps.visible && this.props.visible) {
            this.setState({
                errorMessage: undefined,
                fileName: undefined,
                importName: '',
                importNameError: undefined,
                isReadingClipboard: false,
                isReadingFile: false,
                parseResult: undefined,
                pastedContent: '',
                parsedSourceLabel: undefined,
            });
        }
    }

    onBrowseClick = () => {
        this.fileInputRef.current?.click();
    };

    onFileChanged = async (event: ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0];

        if (!selectedFile) {
            return;
        }

        this.setState({ errorMessage: undefined, fileName: selectedFile.name, importNameError: undefined, isReadingFile: true, parsedSourceLabel: undefined });

        try {
            const fileContent = await selectedFile.text();
            this.applyParsedImportContent(selectedFile.name, fileContent, selectedFile.name);
            this.setState({ isReadingFile: false });
        } catch (error: unknown) {
            this.setState({
                errorMessage: error instanceof Error ? error.message : 'Unable to read the selected file.',
                isReadingFile: false,
                parseResult: undefined,
            });
        }

        event.target.value = '';
    };

    onPastedContentChanged = (event: ChangeEvent<HTMLInputElement>) => {
        this.setState({
            errorMessage: undefined,
            pastedContent: event.target.value,
        });
    };

    onParsePastedContent = () => {
        const normalizedPastedContent = this.state.pastedContent.trim();

        if (!normalizedPastedContent) {
            this.setState({ errorMessage: 'Paste spreadsheet data before importing.' });
            return;
        }

        this.applyParsedImportContent('clipboard-paste.tsv', normalizedPastedContent, 'Clipboard paste');
    };

    onReadClipboardClick = async () => {
        if (!navigator.clipboard?.readText) {
            this.setState({ errorMessage: 'Clipboard access is not available in this browser. Paste directly into the text area instead.' });
            return;
        }

        this.setState({ errorMessage: undefined, isReadingClipboard: true });

        try {
            const clipboardText = await navigator.clipboard.readText();

            this.setState({
                isReadingClipboard: false,
                pastedContent: clipboardText,
            }, this.onParsePastedContent);
        } catch (error: unknown) {
            this.setState({
                errorMessage: error instanceof Error ? error.message : 'Unable to read from the clipboard.',
                isReadingClipboard: false,
            });
        }
    };

    onImportClick = () => {
        const normalizedName = normalizeProgramNameInput(this.state.importName);

        if (!normalizedName) {
            this.setState({ importNameError: 'Program name is required.' });
            return;
        }

        if (!this.state.parseResult?.canImport) {
            return;
        }

        this.props.onImport(normalizedName, this.state.parseResult.weeks);
    };

    onImportNameChanged = (event: ChangeEvent<HTMLInputElement>) => {
        this.setState({
            importName: event.target.value,
            importNameError: undefined,
        });
    };

    applyParsedImportContent(fileName: string, fileContent: string, parsedSourceLabel: string) {
        const parseResult = parseProgramImportFile(fileName, fileContent);

        this.setState({
            errorMessage: undefined,
            importName: parseResult.suggestedProgramName,
            importNameError: undefined,
            parseResult,
            parsedSourceLabel,
        });
    }

    render() {
        const { existingProgramNames, onCancel, visible } = this.props;
        const { errorMessage, fileName, importName, importNameError, isReadingClipboard, isReadingFile, parseResult, parsedSourceLabel, pastedContent } = this.state;
        const normalizedImportName = normalizeProgramNameInput(importName);
        const overwritesExistingProgram = Boolean(normalizedImportName && existingProgramNames.includes(normalizedImportName));

        return (
            <Dialog fullWidth maxWidth="md" open={visible}>
                <DialogTitle>Import Program From CSV/TSV</DialogTitle>
                <DialogContent dividers sx={{ display: 'grid', gap: 2.25 }}>
                    <input accept=".csv,.tsv,text/csv,text/tab-separated-values" hidden onChange={this.onFileChanged} ref={this.fileInputRef} type="file" />
                    <Paper sx={{ borderRadius: 3, p: 2 }} variant="outlined">
                        <Box sx={{ alignItems: { md: 'center', xs: 'stretch' }, display: 'flex', flexDirection: { md: 'row', xs: 'column' }, gap: 1.5, justifyContent: 'space-between' }}>
                            <Box>
                                <Typography sx={{ fontWeight: 700 }} variant="subtitle1">Choose an import file</Typography>
                                <Typography color="text.secondary" variant="body2">
                                    Supports both known program export styles in CSV or TSV, even when headers have inconsistent casing or spacing.
                                </Typography>
                            </Box>
                            <Button disabled={isReadingFile} onClick={this.onBrowseClick} startIcon={<UploadFileRoundedIcon />} variant="contained">
                                {isReadingFile ? 'Reading File...' : 'Choose File'}
                            </Button>
                        </Box>
                        <Typography color="text.secondary" sx={{ mt: 1.5 }} variant="body2">
                            {fileName ? `Selected file: ${fileName}` : 'No file selected yet.'}
                        </Typography>
                    </Paper>
                    <Paper sx={{ borderRadius: 3, p: 2 }} variant="outlined">
                        <Box sx={{ alignItems: { md: 'center', xs: 'stretch' }, display: 'flex', flexDirection: { md: 'row', xs: 'column' }, gap: 1.5, justifyContent: 'space-between', mb: 1.5 }}>
                            <Box>
                                <Typography sx={{ fontWeight: 700 }} variant="subtitle1">Paste From Spreadsheet</Typography>
                                <Typography color="text.secondary" variant="body2">
                                    Copy rows directly from Sheets or Excel, then paste them here. Tab-separated clipboard data is supported.
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: { md: 'flex-end', xs: 'stretch' } }}>
                                <Button disabled={isReadingClipboard} onClick={this.onReadClipboardClick} startIcon={<ContentPasteRoundedIcon />} variant="outlined">
                                    {isReadingClipboard ? 'Reading Clipboard...' : 'Read Clipboard'}
                                </Button>
                                <Button disabled={!pastedContent.trim()} onClick={this.onParsePastedContent} variant="contained">Parse Pasted Data</Button>
                            </Box>
                        </Box>
                        <TextField
                            fullWidth
                            minRows={6}
                            multiline
                            onChange={this.onPastedContentChanged}
                            placeholder={'Paste header and rows here. Example:\nLevel\tWeek\tDay\tDescription\tPrimary Type'}
                            size="small"
                            value={pastedContent}
                        />
                    </Paper>
                    {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
                    {parseResult && (
                        <>
                            <TextField
                                error={Boolean(importNameError)}
                                fullWidth
                                helperText={importNameError}
                                label="Program Name"
                                onChange={this.onImportNameChanged}
                                size="small"
                                value={importName}
                            />
                            {overwritesExistingProgram && (
                                <Alert severity="warning">
                                    A program named {normalizedImportName} already exists. Saving this imported draft will replace that program.
                                </Alert>
                            )}
                            <Paper sx={{ borderRadius: 3, p: 2 }} variant="outlined">
                                <Typography sx={{ fontWeight: 700, mb: 1 }} variant="subtitle1">Import Audit</Typography>
                                {parsedSourceLabel && <Typography variant="body2">Source: {parsedSourceLabel}</Typography>}
                                <Typography variant="body2">Detected format: {parseResult.detectedDelimiter.toUpperCase()}</Typography>
                                <Typography variant="body2">Rows imported into draft: {parseResult.rowsImported}</Typography>
                                <Typography variant="body2">Mapped columns: {parseResult.fieldMappings.filter((mapping) => Boolean(mapping.canonicalField)).length} / {parseResult.fieldMappings.length}</Typography>
                            </Paper>
                            <Paper sx={{ borderRadius: 3, p: 2 }} variant="outlined">
                                <Typography sx={{ fontWeight: 700, mb: 1 }} variant="subtitle1">Header Mapping</Typography>
                                <Box sx={{ display: 'grid', gap: 0.75 }}>
                                    {parseResult.fieldMappings.map((mapping) => (
                                        <Typography key={`${mapping.sourceHeader}-${mapping.normalizedHeader}`} variant="body2">
                                            {mapping.sourceHeader} -&gt; {mapping.canonicalField || 'ignored'}
                                        </Typography>
                                    ))}
                                </Box>
                            </Paper>
                            {parseResult.errors.length > 0 && (
                                <Alert severity="error">
                                    <Box sx={{ display: 'grid', gap: 0.5 }}>
                                        <Typography sx={{ fontWeight: 700 }} variant="body2">Import blocked</Typography>
                                        {parseResult.errors.slice(0, 8).map((issue, index) => (
                                            <Typography key={`${issue.message}-${index}`} variant="body2">
                                                {issue.rowNumber ? `Row ${issue.rowNumber}: ` : ''}{issue.message}
                                            </Typography>
                                        ))}
                                        {parseResult.errors.length > 8 && (
                                            <Typography variant="body2">Additional errors: {parseResult.errors.length - 8}</Typography>
                                        )}
                                    </Box>
                                </Alert>
                            )}
                            {parseResult.warnings.length > 0 && (
                                <Alert severity="warning">
                                    <Box sx={{ display: 'grid', gap: 0.5 }}>
                                        <Typography sx={{ fontWeight: 700 }} variant="body2">Review after import</Typography>
                                        {parseResult.warnings.slice(0, 8).map((issue, index) => (
                                            <Typography key={`${issue.message}-${index}`} variant="body2">
                                                {issue.rowNumber ? `Row ${issue.rowNumber}: ` : ''}{issue.message}
                                            </Typography>
                                        ))}
                                        {parseResult.warnings.length > 8 && (
                                            <Typography variant="body2">Additional warnings: {parseResult.warnings.length - 8}</Typography>
                                        )}
                                    </Box>
                                </Alert>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={onCancel}>Cancel</Button>
                    <Button disabled={!parseResult?.canImport} onClick={this.onImportClick} variant="contained">Import Into Builder</Button>
                </DialogActions>
            </Dialog>
        );
    }
}

export default ProgramImportDialog;